// src/components/JuanScope/OfficiallyEnrolled.js
import React, { useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faCheckCircle,
  faDownload,
  faPrint,
  faSpinner,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import {
  Alert,
  Button,
  Card,
  Divider,
  Space,
  Typography,
  Descriptions,
  Tag,
  Result,
} from "antd";
import SideNavigation from "./SideNavigation";
import SJDEFILogo from "../../images/SJDEFILogo.png";
import useEnrollmentStatus from "../../hooks/userEnrollmentStatus";
import "../../css/JuanScope/ScopeRegistration1.css";
import "./OfficiallyEnrolled.css";

const { Title, Text, Paragraph } = Typography;

function OfficiallyEnrolled() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { userData, applicationStatus, loading, error, handleLogout } =
    useEnrollmentStatus();

  // Get payment reference from localStorage
  const paymentReference = localStorage.getItem("paymentReferenceNumber") || "N/A";
  const paymentCompletedAt = localStorage.getItem("paymentInitiatedAt") 
    ? new Date(localStorage.getItem("paymentInitiatedAt")).toLocaleString() 
    : "N/A";

  // Event handlers
  const toggleSidebar = useCallback(() => setSidebarOpen(!sidebarOpen), [sidebarOpen]);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  // Handle print enrollment details
  const handlePrintEnrollment = useCallback(() => {
    setIsLoading(true);
    window.print();
    setTimeout(() => setIsLoading(false), 1000);
  }, []);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-PH", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <div className="scope-registration-container">
      {/* Header */}
      <header className="juan-register-header">
        <div className="juan-header-left">
          <img
            src={SJDEFILogo}
            alt="SJDEFI Logo"
            className="juan-logo-register"
          />
          <div className="juan-header-text">
            <Title level={1} style={{ margin: 0, color: "inherit" }}>
              JUAN SCOPE
            </Title>
          </div>
        </div>
        <div className="hamburger-menu">
          <Button
            type="text"
            icon={<FontAwesomeIcon icon={sidebarOpen ? faTimes : faBars} />}
            onClick={toggleSidebar}
            aria-label="Toggle navigation menu"
            size="large"
          />
        </div>
      </header>

      <div className="scope-registration-content">
        <SideNavigation
          userData={userData}
          onNavigate={closeSidebar}
          isOpen={sidebarOpen}
          {...applicationStatus}
        />

        <main
          className={`scope-main-content ${sidebarOpen ? "sidebar-open" : ""}`}
        >
          <div className="registration-content">
            <Title level={2} className="registration-title">
              Officially Enrolled
            </Title>
            <Divider />

            <div className="registration-container">
              {/* Success Banner */}
              <Result
                status="success"
                icon={<FontAwesomeIcon icon={faCheckCircle} style={{ color: '#52c41a', fontSize: '72px' }} />}
                title="Congratulations! You are now officially enrolled!"
                subTitle="Your enrollment has been confirmed and payment has been received. Welcome to San Juan De Dios Educational Foundation Inc.!"
                extra={[
                  <Button
                    key="print"
                    type="primary"
                    icon={<FontAwesomeIcon icon={isLoading ? faSpinner : faPrint} spin={isLoading} />}
                    onClick={handlePrintEnrollment}
                    size="large"
                  >
                    Print Enrollment Details
                  </Button>,
                  <Button
                    key="download"
                    icon={<FontAwesomeIcon icon={faDownload} />}
                    size="large"
                  >
                    Download Enrollment Certificate
                  </Button>,
                ]}
              />

              <div className="enrollment-details-section">
                <Card 
                  title={<Title level={4}>Enrollment Information</Title>}
                  className="enrollment-card"
                >
                  <Descriptions bordered column={{ xs: 1, sm: 2, md: 2, lg: 2 }}>
                    <Descriptions.Item label="Student Name">
                      {userData?.firstName} {userData?.middleName} {userData?.lastName}
                    </Descriptions.Item>
                    <Descriptions.Item label="Student ID">
                      {userData?.studentID || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Academic Year">
                      {userData?.academicYear || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Academic Term">
                      {userData?.academicTerm || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Academic Level">
                      {userData?.academicLevel || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Academic Strand">
                      {userData?.academicStrand || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Enrollment Status">
                      <Tag color="green" style={{ fontSize: '14px', padding: '4px 8px' }}>
                        OFFICIALLY ENROLLED
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Enrollment Date">
                      {formatDate(new Date().toISOString())}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>

                <Card 
                  title={<Title level={4}>Payment Information</Title>}
                  className="payment-card"
                >
                  <Descriptions bordered column={{ xs: 1, sm: 2, md: 2, lg: 2 }}>
                    <Descriptions.Item label="Payment Reference">
                      {paymentReference}
                    </Descriptions.Item>
                    <Descriptions.Item label="Payment Date">
                      {paymentCompletedAt}
                    </Descriptions.Item>
                    <Descriptions.Item label="Payment Status">
                      <Tag color="green" style={{ fontSize: '14px', padding: '4px 8px' }}>
                        COMPLETED
                      </Tag>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>

                <Alert
                  message="Important Next Steps"
                  description={
                    <div>
                      <Paragraph>
                        Please complete the following steps to prepare for your classes:
                      </Paragraph>
                      <ol>
                        <li>Attend the student orientation on August 25, 2025</li>
                        <li>Download your class schedule from the student portal</li>
                        <li>Prepare your learning materials and supplies</li>
                        <li>Join the official student communication channels</li>
                      </ol>
                    </div>
                  }
                  type="info"
                  showIcon
                  style={{ marginTop: '2rem' }}
                />
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay active" onClick={toggleSidebar}></div>
      )}

      {/* Logout Modal */}
      {showLogoutModal && (
        <div className="scope-modal-overlay">
          <div className="scope-confirm-modal">
            <Title level={3}>Confirm Logout</Title>
            <Text>Are you sure you want to logout?</Text>
            <div className="scope-modal-buttons" style={{ marginTop: "1rem" }}>
              <Space>
                <Button onClick={() => setShowLogoutModal(false)}>
                  Cancel
                </Button>
                <Button type="primary" danger onClick={handleLogout}>
                  Logout
                </Button>
              </Space>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OfficiallyEnrolled;
